// src/hooks/user/useFollowersList.js
import { useQuery, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { getFollowers } from "../../utils/follow/followApi";
import {
  FOLLOW_CACHE_CONFIG,
  FOLLOW_RETRY_CONFIG,
  followQueryKeys,
  followPerformanceUtils,
} from "../../utils/follow/queryConfig";

/**
 * Custom hook for fetching paginated list of followers for a user
 *
 * Implements optimized pagination, caching, and bulk follow status prefetching.
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 *
 * @param {string} userId - The ID of the user to get followers for
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Number of followers per page (default: 20)
 * @param {Object} options - Query options
 * @param {boolean} options.prefetchFollowStatus - Whether to prefetch follow status for users (default: true)
 * @returns {Object} Query result with paginated followers data
 */
export const useFollowersList = (
  userId,
  page = 1,
  limit = 20,
  options = {}
) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { prefetchFollowStatus = true, ...queryOptions } = options;

  return useQuery(
    followQueryKeys.followers(userId, page, limit),
    async () => {
      const startTime = Date.now();

      try {
        const token = await getAccessTokenSilently();
        const result = await getFollowers(userId, page, limit, token);

        followPerformanceUtils.trackQueryPerformance(
          followQueryKeys.followers(userId, page, limit),
          startTime
        );

        return result;
      } catch (error) {
        followPerformanceUtils.trackQueryPerformance(
          followQueryKeys.followers(userId, page, limit),
          startTime
        );
        throw error;
      }
    },
    {
      enabled: !!getAccessTokenSilently && !!userId,
      ...FOLLOW_CACHE_CONFIG.userLists,
      ...FOLLOW_RETRY_CONFIG.standard,
      // Prefetch follow status for better UX
      onSuccess: async (data) => {
        if (prefetchFollowStatus && data?.users?.length > 0) {
          try {
            const userIds = data.users.map((user) => user._id).filter(Boolean);

            if (userIds.length > 0) {
              // Prefetch bulk follow status for the users in this page
              queryClient.prefetchQuery({
                queryKey: followQueryKeys.bulkFollowStatus(userIds),
                queryFn: async () => {
                  const token = await getAccessTokenSilently();
                  const { getBulkFollowStatus } = await import(
                    "../../utils/follow/followApi"
                  );
                  return getBulkFollowStatus(userIds, token);
                },
                ...FOLLOW_CACHE_CONFIG.bulkFollowStatus,
              });
            }
          } catch (error) {
            // Silently fail prefetch to not affect main functionality
            console.debug(
              "Failed to prefetch follow status for followers:",
              error
            );
          }
        }

        // Prefetch next page for better UX
        if (data?.pagination?.hasNext) {
          queryClient.prefetchQuery({
            queryKey: followQueryKeys.followers(userId, page + 1, limit),
            queryFn: async () => {
              const token = await getAccessTokenSilently();
              return getFollowers(userId, page + 1, limit, token);
            },
            ...FOLLOW_CACHE_CONFIG.userLists,
          });
        }
      },
      ...queryOptions,
    }
  );
};
